import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { hashKey } from "@tanstack/react-query";
import { BillFilters } from "@common/contracts/bill.contract";
import { Bill, BillSortValue } from "@common/types/bill";
import {
    getBillsBodyQueryKey,
    loadBillsBody,
    useBillsQuery,
} from "@api/bill.api";
import {
    BILLS_TABLE_PAGE_SIZE,
    PaginatedBillsTableContext,
    toPaginatedBillsTableContextValue,
} from "@components/bills/PaginatedBillsTable.context";
import { useSort } from "@components/bills/useSort";
import type { PaginationType } from "@hooks/paginationMode";
import { useLoadMorePages } from "@hooks/useLoadMorePages";
import { useLoadMoreTableState } from "@hooks/useLoadMoreTableState";
import { useScope } from "@state/scopeContext.lib";

const EMPTY_BILLS_PAGE = { pageCount: 1, total: 0, data: [] };

const selectBills = (response: { data: Bill[] }) => response.data;
const getBillId = (bill: Bill) => bill.id;

interface Props {
    /** `null` while the table must not query (a sticky task's membership is unknown). */
    filters: BillFilters | null;
    /** Numbered pages by default; the task view loads more on scroll. */
    paginationType?: PaginationType;
}

/**
 * Feeds the bills table with the bills a task covers. Unlike the bills page it
 * owns its filters, sort and page, and sends the filters in the request body so
 * a task's pinned bill ids do not have to fit in a URL.
 */
export const BillsTaskTableProvider = ({
    filters,
    paginationType = "pages",
    children,
}: PropsWithChildren<Props>) => {
    const isLoadMore = paginationType === "loadMore";
    const filtersKey = hashKey([filters]);
    const [pageState, setPageState] = useState({ key: filtersKey, page: 1 });
    // A changed filter set is a different result set: start from its first page.
    const page =
        !isLoadMore && pageState.key === filtersKey ? pageState.page : 1;
    const { sortExpression, currentSort, handleSortChange } = useSort();
    const { workspaceId } = useScope();
    const isQueryEnabled = filters !== null;

    // In load-more mode this is the first batch, which carries the total.
    const bills = useBillsQuery(
        {
            filters: filters ?? undefined,
            sort: sortExpression,
            page,
            limit: BILLS_TABLE_PAGE_SIZE,
        },
        { enabled: isQueryEnabled },
    );

    const loadedBills = useLoadMorePages({
        buildPageQuery: (pageNumber) => {
            const query = {
                filters: filters ?? undefined,
                sort: sortExpression,
                page: pageNumber,
                limit: BILLS_TABLE_PAGE_SIZE,
            };
            return {
                queryKey: getBillsBodyQueryKey(workspaceId ?? undefined, query),
                queryFn: () => loadBillsBody(workspaceId ?? "", query),
            };
        },
        select: selectBills,
        getRowId: getBillId,
        pageSize: BILLS_TABLE_PAGE_SIZE,
        total: bills.data?.total,
        enabled: isLoadMore && isQueryEnabled && !!workspaceId,
    });
    const loadMoreState = useLoadMoreTableState(loadedBills);
    const loadedRows = loadedBills.rows;

    const billsPage = useMemo(() => {
        if (!isLoadMore || !bills.data) {
            return bills.data;
        }
        return {
            pageCount: 1,
            total: bills.data.total,
            data: loadedRows ?? [],
        };
    }, [isLoadMore, bills.data, loadedRows]);

    const setPage = useCallback(
        (next: number | null | undefined) =>
            setPageState({ key: filtersKey, page: next ?? 1 }),
        [filtersKey],
    );
    const handleSortChangeWithPageReset = useCallback(
        (value: BillSortValue) => {
            handleSortChange(value);
            setPage(1);
        },
        [handleSortChange, setPage],
    );

    const contextValue = useMemo(
        () =>
            toPaginatedBillsTableContextValue({
                page,
                billsPage: filters === null ? EMPTY_BILLS_PAGE : billsPage,
                setPage,
                handleSortChange: handleSortChangeWithPageReset,
                currentSort,
                isLoading: filters !== null && bills.isLoading,
                loadMore: isLoadMore ? loadMoreState : undefined,
            }),
        [
            page,
            billsPage,
            bills.isLoading,
            filters,
            setPage,
            handleSortChangeWithPageReset,
            currentSort,
            isLoadMore,
            loadMoreState,
        ],
    );

    return (
        <PaginatedBillsTableContext.Provider value={contextValue}>
            {children}
        </PaginatedBillsTableContext.Provider>
    );
};
